import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/utils/cn';

interface FieldShellProps {
  label: string;
  htmlFor: string;
  error?: string | undefined;
  trailing?: ReactNode;
  children: ReactNode;
  className?: string;
}

function FieldShell({ label, htmlFor, error, trailing, children, className }: FieldShellProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={htmlFor} className="text-xs font-medium text-ink-600 dark:text-ink-300">
          {label}
        </label>
        {trailing}
      </div>
      {children}
      {error && (
        <p role="alert" className="text-xs text-bad-600 dark:text-bad-400">
          {error}
        </p>
      )}
    </div>
  );
}

export interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  error?: string | undefined;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, error, className, children, id, ...rest },
  ref,
) {
  const autoId = useId();
  const selectId = id ?? autoId;
  return (
    <FieldShell label={label} htmlFor={selectId} error={error} className={className}>
      <div className="relative">
        <select
          ref={ref}
          id={selectId}
          aria-invalid={error ? true : undefined}
          className="field-input appearance-none pr-8"
          {...rest}
        >
          {children}
        </select>
        <ChevronDown
          aria-hidden
          className="pointer-events-none absolute top-1/2 right-2.5 size-4 -translate-y-1/2 text-ink-400"
        />
      </div>
    </FieldShell>
  );
});

export interface NumberFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string | undefined;
  suffix?: string;
}

export const NumberField = forwardRef<HTMLInputElement, NumberFieldProps>(function NumberField(
  { label, error, suffix, className, id, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <FieldShell label={label} htmlFor={inputId} error={error} className={className}>
      <div className="relative">
        <input
          ref={ref}
          id={inputId}
          type="number"
          inputMode="numeric"
          aria-invalid={error ? true : undefined}
          className={cn('field-input', suffix && 'pr-9')}
          {...rest}
        />
        {suffix && (
          <span
            aria-hidden
            className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 font-mono text-xs text-ink-400"
          >
            {suffix}
          </span>
        )}
      </div>
    </FieldShell>
  );
});

export interface SliderFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** Rendered as the live mono readout beside the label, e.g. "15 fps". */
  valueLabel: string;
  error?: string | undefined;
}

export const SliderField = forwardRef<HTMLInputElement, SliderFieldProps>(function SliderField(
  { label, valueLabel, error, className, id, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <FieldShell
      label={label}
      htmlFor={inputId}
      error={error}
      className={className}
      trailing={
        <output htmlFor={inputId} className="font-mono text-xs text-ink-500 dark:text-ink-400">
          {valueLabel}
        </output>
      }
    >
      <input ref={ref} id={inputId} type="range" className="slider" {...rest} />
    </FieldShell>
  );
});
